import { forwardRef } from "react";
import Tooltip from "../../tooltip/Tooltip";
import "./_text-input-with-prefix.scss";

/**
 * Pure-UI text input with a fixed, non-editable prefix segment (e.g. a URL
 * base like "https://app.kribana.com/" before an editable slug). Same
 * conventions as TextInput: onChange(value), composable tooltip.
 */
const TextInputWithPrefix = forwardRef(function TextInputWithPrefix(
  {
    id,
    label,
    prefix,
    value,
    onChange,
    onBlur,
    error,
    disabled = false,
    tooltip,
    placeholder = "",
    compact = false,
    className = "",
    ...rest
  },
  ref
) {
  // Label always renders above the field now - see TextInput's identical
  // comment. `compact` (table cells) skips it entirely, same reasoning too.
  const showLabel = Boolean(label) && !compact;

  return (
    <Tooltip
      content={tooltip}
      disabled={!tooltip}
      className="text-input-with-prefix-tooltip-anchor"
    >
      <div
        className={`text-input-with-prefix ${error ? "text-input-with-prefix--error" : ""} ${
          disabled ? "text-input-with-prefix--disabled" : ""
        } ${compact ? "text-input-with-prefix--compact" : ""} ${className}`}
      >
        {showLabel && (
          <label className="text-input-with-prefix__label" htmlFor={id}>
            {label}
          </label>
        )}
        <div className="text-input-with-prefix__field-wrapper">
          <span className="text-input-with-prefix__prefix" title={prefix}>
            {prefix}
          </span>
          <input
            ref={ref}
            id={id}
            type="text"
            className="text-input-with-prefix__field"
            value={value ?? ""}
            // `compact` never renders a label element - falls back to the
            // label as its placeholder, same as TextInput.
            placeholder={compact ? placeholder || label : placeholder}
            // readOnly, not disabled - see TextInput/index.jsx for why:
            // native `disabled` suppresses mouse events, which breaks the
            // composed Tooltip's hover handlers.
            readOnly={disabled}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : undefined}
            autoComplete="off"
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
            {...rest}
          />
        </div>
        {error && <p className="text-input-with-prefix__error">{error}</p>}
      </div>
    </Tooltip>
  );
});

export default TextInputWithPrefix;
